import { GUIDE_LEAD_SECONDS, GUIDE_STALL_SECONDS } from './guide.js';

/**
 * Decision 11 of the voice-guide design (ADR-035): a voice asked for and not
 * begun `GUIDE_STALL_SECONDS` after it was due -- a slow fetch, a file that
 * never comes -- leaves the text box for the rest of the step. It is armed
 * when a step's panel shows, and again whenever the voice is asked to start
 * over (Voorlezen back on, the class resuming after a pause, "Nog eens", the
 * guide switched to voice): what counts is a start *since* it was armed, so a
 * voice that played before does not hide one that fails now.
 *
 * Arm it before asking the voice to start: a decoded voice can begin at once.
 */

export type StallOptions = {
  /** Runs `run` after `seconds`; returns a cancel. */
  schedule: (run: () => void, seconds: number) => () => void;
  /** How many times the voice has begun, ever (VoicePlayer's `starts`). */
  starts: () => number;
  /** Whether a stall can count now: guided by the voice, the class's Voorlezen on, not paused. */
  applies: () => boolean;
  /** The voice is late: the text box for the rest of the step. */
  onStall: () => void;
};

export class StallWatch {
  readonly #options: StallOptions;
  #cancel: (() => void) | undefined;

  constructor(options: StallOptions) {
    this.#options = options;
  }

  /** Watches for a start within `seconds` (by default the lead-in and the stall's 3 s). */
  arm(seconds = GUIDE_LEAD_SECONDS + GUIDE_STALL_SECONDS): void {
    this.cancel();
    const before = this.#options.starts();
    const cancel = this.#options.schedule(() => {
      if (this.#cancel !== cancel) return;
      this.#cancel = undefined;
      if (this.#options.starts() > before || !this.#options.applies()) return;
      this.#options.onStall();
    }, seconds);
    this.#cancel = cancel;
  }

  cancel(): void {
    this.#cancel?.();
    this.#cancel = undefined;
  }
}
